/**
 * Cibles (Targets) colour tokens → hex.
 *
 * D45: colour is a display attribute of a `marque` target, resolved by the
 * CONSUMER on `rootMarqueId` — the matcher and the persisted match never carry a
 * hex. Two consumers now: the Targets table (the picker and the row dot) and the
 * Brand Perception response highlighter. One copy, because two frontend copies of
 * a shared constant have drifted silently before (CLAUDE.md, BRAND_STRATEGY_LIMITS).
 */
import type { TargetColorToken, TargetHighlight, TargetSourceHighlight } from '@traaker/shared';

/** Tailwind 500 values, in `TARGET_COLOR_PALETTE` order. */
export const TARGET_COLOR_HEX: Record<TargetColorToken, string> = {
  blue:    '#3B82F6',
  indigo:  '#6366F1',
  violet:  '#8B5CF6',
  purple:  '#A855F7',
  fuchsia: '#D946EF',
  pink:    '#EC4899',
  rose:    '#F43F5E',
  red:     '#EF4444',
  orange:  '#F97316',
  amber:   '#F59E0B',
  yellow:  '#EAB308',
  lime:    '#84CC16',
  green:   '#22C55E',
  emerald: '#10B981',
  teal:    '#14B8A6',
  cyan:    '#06B6D4',
  sky:     '#0EA5E9',
};

/** "No colour" — a target's colour is optional and most have none. */
export const TARGET_COLOR_GREY = '#9CA3AF';

export function targetColorHex(token?: string | null): string {
  if (!token) return TARGET_COLOR_GREY;
  return (TARGET_COLOR_HEX as Record<string, string>)[token] ?? TARGET_COLOR_GREY;
}

/**
 * The Cibles terms matched in one response, prepared for the highlighter.
 *
 * The matcher gave us TERMS, not char offsets, and it had to: the renderer rewrites
 * the answer and re-joins each paragraph's lines with ' ' before any span could be
 * drawn, so an absolute offset into the stored answer is stale by the time it is
 * rendered. Terms are offset-free — the highlighter re-finds every occurrence
 * itself, with the same fold it already uses for the brand name.
 */
export type TargetTerms = {
  /** Already folded + lowercased by the matcher (D42). Longest first, so a
   *  multi-word term wins the regex alternation over its own first word. */
  terms: string[];
  /** term → hex (D45). Every term has one: a marque with no colour picked yet
   *  resolves to the neutral grey, so a target always reads as a target. */
  colors: Map<string, string>;
  /**
   * term → the co-occurrence condition that must hold AT THE OCCURRENCE for it to be
   * painted (2026-09-23). Only pair halves are in here; a term absent from this map is
   * unconditional.
   *
   * Response-level allow/deny would not do. The answer this was built from named the
   * brand's Avenue Louise branch AND a competitor sited on the same avenue, so the word
   * "Louise" has to be painted at one occurrence and left alone at the other — see
   * `isPairSatisfied`.
   */
  gates: Map<string, { pairWith: string; window: number }>;
};

export const NO_TARGET_TERMS: TargetTerms = { terms: [], colors: new Map(), gates: new Map() };

/**
 * Fold a row's `targetHighlights` into the term list + colour map the highlighter
 * consumes. `marqueColors` is `rootMarqueId → colour token`, fetched once per brand;
 * a term whose marque has no colour resolves to grey via `targetColorHex`, so it is
 * still visibly a target — just not attributed to a particular marque yet.
 */
export function targetTermsFrom(
  highlights: TargetHighlight[] | undefined,
  marqueColors: Record<string, string>,
): TargetTerms {
  const hl = highlights || [];
  if (hl.length === 0) return NO_TARGET_TERMS;
  const colors = new Map<string, string>();
  const gates = new Map<string, { pairWith: string; window: number }>();
  const terms: string[] = [];
  for (const h of hl) {
    // An empty term would compile to a regex alternative matching everywhere.
    const term = (h.value || '').trim();
    if (!term || colors.has(term)) continue;
    // A conditional highlight whose condition can't be evaluated must be DROPPED, not
    // promoted to unconditional — an ungated pair half is what painted a competitor's
    // address in the brand's colour. Old rows persisted before 2026-09-23 carry no
    // `pairWith`, so they simply stop highlighting their pair halves; the match itself
    // is untouched and the row still reads as cited.
    if (h.pairWith !== undefined && !(h.pairWith && h.window && h.window > 0)) continue;
    terms.push(term);
    colors.set(term, targetColorHex(h.rootMarqueId ? marqueColors[h.rootMarqueId] : undefined));
    if (h.pairWith) gates.set(term, { pairWith: h.pairWith, window: h.window as number });
  }
  terms.sort((a, b) => b.length - a.length);
  return { terms, colors, gates };
}

/**
 * Abbreviations whose trailing '.' is NOT a sentence end — the mirror of
 * `search.py::_ABBREV_BEFORE_DOT`, and it must stay identical to it. "Dessange, av.
 * Louise" is how an engine ordinarily writes an address; reading that dot as a full
 * stop would drop the highlight the matcher kept, so the two sides would disagree on
 * the same row.
 */
const ABBREV_BEFORE_DOT = new Set([
  'av', 'ave', 'bd', 'bld', 'bvd', 'blvd', 'pl', 'rte', 'ch', 'imp', 'sq',
  'st', 'ste', 'sts', 'rd', 'str',
  'no', 'nos', 'nr', 'num', 'bte', 'app', 'appt', 'etg', 'cp', 'bp', 'zi', 'za',
  'm', 'mm', 'mme', 'mmes', 'mr', 'mrs', 'ms', 'dr', 'pr', 'prof', 'me', 'mgr',
  'etc', 'cf', 'ex', 'env', 'vs', 'inc', 'ltd', 'sa', 'sas', 'sarl', 'srl',
  'co', 'corp', 'dept', 'fig', 'vol', 'min', 'max', 'tel', 'fax',
]);

/**
 * Does a sentence end anywhere in `hay[lo, hi)`? Mirrors `search.py::sentence_end_offsets`.
 *
 * The optional digits after the dot are the engines' footnote marker glued to the stop
 * ("…complète.8 Le salon…"), which is the common shape in a cited answer. A decimal does
 * not match: '3.5' has a digits-only word before the dot, dropped below.
 */
const SENTENCE_END = /;|\.(?:\d{1,2})?(?=\s|$)/g;

function endsSentenceWithin(hay: string, lo: number, hi: number): boolean {
  // Scanned over the WHOLE segment with a start offset, not over a slice: the regex's
  // `$` would otherwise fire on a dot that merely ends the slice, and the word before
  // the dot has to be read from text the slice does not contain.
  SENTENCE_END.lastIndex = Math.max(0, lo);
  for (let m = SENTENCE_END.exec(hay); m !== null && m.index < hi; m = SENTENCE_END.exec(hay)) {
    if (hay[m.index] === ';') return true;
    const i = m.index;
    // Unicode letters, to mirror Python's `str.isalnum()`: the matcher measures folded
    // text where 'é' is already 'e', but this function is also handed text that is only
    // lowercased, and treating 'é' as a non-letter would read "réputé." as a dot with no
    // word before it — i.e. not a sentence end at all.
    let j = i - 1;
    while (j >= 0 && /[\p{L}\p{N}'-]/u.test(hay[j])) j--;
    const word = hay.slice(j + 1, i);
    // A bare number is a list bullet or a decimal, a single letter is an initial.
    if (!word || /^\d+$/.test(word) || word.length === 1 || ABBREV_BEFORE_DOT.has(word)) continue;
    return true;
  }
  return false;
}

/**
 * Is a gated term's partner close enough to THIS occurrence of it, and in the same
 * sentence as it?
 *
 * `window` is the matcher's `_gap` — characters BETWEEN the two halves, not
 * start-to-start (targets.md §11.1) — and it is the value the index shipped for that
 * rung (80 for a street, 25 for a city), never a constant of our own. The sentence
 * test is the second half of the same gate, added 2026-09-23 because 80 characters can
 * step over a full stop: it must stay in step with `search.py::_crosses_sentence_end`,
 * since a highlight is the user-visible claim about a verdict computed there.
 *
 * Two known ways this is stricter than the matcher, both of which lose a highlight
 * rather than inventing one:
 *
 *   * `folded` here is one rendered SEGMENT, not the whole answer, so a partner sitting
 *     on the far side of a `**bold**` boundary is invisible to us;
 *   * the matcher collapses whitespace before measuring and we do not, so a gap padded
 *     with redundant spaces reads longer here.
 *
 * Failing closed is the right direction: a missed highlight is a cosmetic loss, while a
 * false one attributes a competitor to the client's brand.
 */
export function isPairSatisfied(
  folded: string,
  start: number,
  end: number,
  gate: { pairWith: string; window: number },
): boolean {
  const hay = folded.toLowerCase();
  const needle = gate.pairWith.toLowerCase();
  for (let at = hay.indexOf(needle); at !== -1; at = hay.indexOf(needle, at + 1)) {
    const after = at >= end;
    const gap = after ? at - end : start - (at + needle.length);
    // gap < 0 means the two overlap, which is not a co-occurrence of two halves.
    if (gap < 0 || gap > gate.window) continue;
    const [lo, hi] = after ? [end, at] : [at + needle.length, start];
    if (!endsSentenceWithin(hay, lo, hi)) return true;
  }
  return false;
}

/**
 * Per-category accent colours for the BP engine panel badges (2026-09-23).
 * One canonical copy (D45 principle): no second table in Targets.tsx.
 * Colours match the CATEGORIES table in Targets.tsx and are deliberately
 * NOT marque-colour tokens — they are fixed per category, not per tenant.
 */
export const CATEGORY_CHIP_COLOR: Record<string, { bg: string; fg: string }> = {
  offre:          { bg: '#F5F3FF', fg: '#7C3AED' },
  produit:        { bg: '#F0FDFA', fg: '#0D9488' },
  collection:     { bg: '#F0FDFA', fg: '#0D9488' },
  contenu:        { bg: '#EFF6FF', fg: '#2563EB' },
  etablissement:  { bg: '#ECFDF5', fg: '#059669' },
  personne:       { bg: '#FDF2F8', fg: '#DB2777' },
};

/**
 * `sourceIndex → hex` for the source chips — the citation-side counterpart of
 * `targetTermsFrom`. Same grey default: a matched source whose marque has no colour
 * still reads as matched.
 */
export function sourceColorsFrom(
  highlights: TargetSourceHighlight[] | undefined,
  marqueColors: Record<string, string>,
): Map<number, string> {
  const out = new Map<number, string>();
  for (const h of highlights || []) {
    if (out.has(h.sourceIndex)) continue;
    out.set(h.sourceIndex, targetColorHex(h.rootMarqueId ? marqueColors[h.rootMarqueId] : undefined));
  }
  return out;
}
